import React from 'react';

import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { FloatingLabelComponent } from 'design/floatingLabel';
import { TextInputControlComponent } from 'design/textInputControl';
import { TextComponent } from 'design/text/textComponents';
import {
  PasswordChangeFormValues,
  PasswordChangeFormViolation,
} from 'types/passwordChangeFormTypes';
import { createPasswordChangeFormViolationLense } from 'utils/passwordChangeFormUtils';

type Props = {
  values: PasswordChangeFormValues;
  violations: PasswordChangeFormViolation[];
  disabled: boolean;
  hasCurrentPasswordField?: boolean;
  onNextPasswordChange(nextValue: string): void;
  onNextPasswordRepeatChange(nextValue: string): void;
  onCurrentPasswordChange(nextValue: string): void;
};

const CURRENT_PASSWORD_MAX_LENGTH = 100;
const NEXT_PASSWORD_MAX_LENGTH = 100;
const NEXT_PASSWORD_REPEAT_MAX_LENGTH = 100;

export const PasswordChangeFieldset = (props: Props) => {
  const {
    hasCurrentPasswordField = false,
    disabled,
    values,
    violations,
    onNextPasswordChange,
    onNextPasswordRepeatChange,
    onCurrentPasswordChange,
  } = props;

  const {
    inputValidationRequired,
    inputValidationPasswordsMatch,
    inputValidationWrongPassword,
    inputValidationPassswordLength,
    passwordChangeForm: ownDictionary,
  } = useIntlDictionary();

  const currentPasswordHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'CurrentPasswordMissed':
          return inputValidationRequired;
        case 'CurrentPasswordDoNotMatch':
          return inputValidationWrongPassword;
      }
    }
  }, [inputValidationRequired, inputValidationWrongPassword, violations]);

  const currentPasswordIsInvalid = violations.some((violation) =>
    createPasswordChangeFormViolationLense(
      violation
    ).isCurrentPasswordViolation()
  );

  const nextPasswordHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'NextPasswordMissed':
          return inputValidationRequired;
        case 'NextPasswordTooShort':
          return inputValidationPassswordLength;
      }
    }
  }, [inputValidationPassswordLength, inputValidationRequired, violations]);

  const nextPasswordIsInvalid = violations.some((violation) =>
    createPasswordChangeFormViolationLense(violation).isNextPasswordViolation()
  );

  const nextPasswordRepeatHint = React.useMemo(() => {
    for (let violation of violations) {
      switch (violation.name) {
        case 'NextPasswordRepeatMissed':
          return inputValidationRequired;
        case 'NextPasswordRepeatDoNotMatch':
          return inputValidationPasswordsMatch;
      }
    }
  }, [inputValidationPasswordsMatch, inputValidationRequired, violations]);

  const nextPasswordRepeatIsInvalid = violations.some((violation) =>
    createPasswordChangeFormViolationLense(
      violation
    ).isNextPasswordRepeatViolation()
  );

  return (
    <>
      {hasCurrentPasswordField && (
        <FloatingLabelComponent
          lifted={!!values.currentPassword}
          invalid={currentPasswordIsInvalid}
          titleText={
            <TextComponent color="secondary" size="m">
              {ownDictionary.currentPasswordInputLabel}
            </TextComponent>
          }
          hintText={
            <TextComponent color="critical" size="s">
              {currentPasswordHint}
            </TextComponent>
          }
        >
          <TextInputControlComponent
            name="currentPassword"
            type="password"
            autoFocus
            value={values.currentPassword}
            disabled={disabled}
            invalid={currentPasswordIsInvalid}
            maxLength={CURRENT_PASSWORD_MAX_LENGTH}
            onValueChange={onCurrentPasswordChange}
          />
        </FloatingLabelComponent>
      )}
      <FloatingLabelComponent
        lifted={!!values.nextPassword}
        invalid={nextPasswordIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.nextPasswordInputLabel}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {nextPasswordHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="nextPassword"
          type="password"
          value={values.nextPassword}
          disabled={disabled}
          invalid={nextPasswordIsInvalid}
          maxLength={NEXT_PASSWORD_MAX_LENGTH}
          onValueChange={onNextPasswordChange}
        />
      </FloatingLabelComponent>
      <FloatingLabelComponent
        lifted={!!values.nextPasswordRepeat}
        invalid={nextPasswordRepeatIsInvalid}
        titleText={
          <TextComponent color="secondary" size="m">
            {ownDictionary.nextPasswordRepeatInputLabel}
          </TextComponent>
        }
        hintText={
          <TextComponent color="critical" size="s">
            {nextPasswordRepeatHint}
          </TextComponent>
        }
      >
        <TextInputControlComponent
          name="nextPasswordRepeat"
          type="password"
          value={values.nextPasswordRepeat}
          disabled={disabled}
          invalid={nextPasswordRepeatIsInvalid}
          maxLength={NEXT_PASSWORD_REPEAT_MAX_LENGTH}
          onValueChange={onNextPasswordRepeatChange}
        />
      </FloatingLabelComponent>
    </>
  );
};
